function ExternalLink({ href, children }: { href: string; children: string }) {
  return (
    <a
      href={href}
      className="font-medium text-primary underline underline-offset-2 hover:no-underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

import { SEO } from '@/components/SEO';

export default function TermsOfServicePage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <SEO
        title="Términos y condiciones — LÍDER IA"
        description="Términos y Condiciones de uso de LÍDER IA, operada por VIVETORI S.A.S."
        path="/terms-of-service"
      />
      <header className="mb-10">
        <h1 className="mb-3 text-4xl font-bold tracking-tight text-foreground">Términos y Condiciones</h1>
        <p className="text-muted-foreground">
          Términos y Condiciones de uso de LIDER IA, operada por VIVETORI S.A.S.
        </p>
      </header>

      <article className="prose prose-sm max-w-none text-foreground prose-headings:scroll-mt-24 prose-headings:font-semibold prose-p:text-foreground/90 prose-li:text-foreground/90 prose-strong:text-foreground">
        <h3>1. Información general y aceptación</h3>
        <h4>1.1. Identificación del responsable</h4>
        <p>
          La presente plataforma digital, disponible en{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, es operada por la
          sociedad <strong>VIVETORI S.A.S.</strong>, identificada con NIT 901560075, domiciliada en Colombia, y titular de
          la marca comercial <strong>LIDER IA</strong>.
        </p>
        <p>Para efectos de notificaciones legales o contacto formal, se ha habilitado el siguiente canal:</p>
        <p>
          <strong>Correo electrónico:</strong>{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
        </p>

        <h4>1.2. Aceptación de los Términos y Condiciones</h4>
        <p>
          El presente documento establece los términos legales y condiciones de uso que regulan el acceso, navegación y
          utilización de la plataforma LIDER IA, así como la contratación de sus servicios, suscripciones o rutas de
          certificación.
        </p>
        <p>
          Al registrarse, acceder, utilizar o realizar cualquier acción dentro de la plataforma, el usuario acepta
          expresamente estos Términos y Condiciones. En caso de no estar de acuerdo con su contenido, deberá abstenerse
          de utilizar los servicios ofrecidos.
        </p>
        <p>
          Estos Términos y Condiciones constituyen un acuerdo vinculante entre el usuario y VIVETORI S.A.S. – LIDER IA,
          y son aplicables a todas las personas naturales o jurídicas que accedan a la plataforma, independientemente del
          país desde el cual se conecten.
        </p>

        <h3>2. Definiciones</h3>
        <p>Para facilitar la comprensión de estos Términos y Condiciones, se establecen las siguientes definiciones:</p>
        <dl className="not-prose space-y-4 text-sm">
          <div>
            <dt className="font-semibold text-foreground">LIDER IA</dt>
            <dd className="mt-1 text-muted-foreground">
              Marca comercial de propiedad de VIVETORI S.A.S., bajo la cual se ofrece la plataforma tecnológica de
              formación, certificación y acompañamiento en inteligencia artificial para empresas y profesionales.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">La Plataforma</dt>
            <dd className="mt-1 text-muted-foreground">
              Sitio web y sistema digital accesible desde{' '}
              <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, en el cual los
              usuarios acceden a contenidos, herramientas, rutas y servicios de LIDER IA.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Usuario</dt>
            <dd className="mt-1 text-muted-foreground">
              Persona natural o jurídica que se registra en la plataforma y hace uso de sus servicios, ya sea como
              suscriptor, participante de una ruta de certificación o visitante.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Cuenta de Usuario</dt>
            <dd className="mt-1 text-muted-foreground">
              Registro individual asociado a una dirección de correo electrónico, desde el cual se accede a los
              contenidos y funcionalidades contratadas dentro de la plataforma.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Contenidos</dt>
            <dd className="mt-1 text-muted-foreground">
              Todo material digital disponible en la plataforma, incluyendo cápsulas de aprendizaje, guías, recursos
              descargables, instructivos, plantillas, simuladores, y demás materiales formativos o informativos.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Rutas de Certificación</dt>
            <dd className="mt-1 text-muted-foreground">
              Rutas de formación ofrecidas por LIDER IA con pago único, que otorgan al usuario una certificación tras
              completar satisfactoriamente los módulos, ejercicios y evaluaciones requeridas.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Suscripción</dt>
            <dd className="mt-1 text-muted-foreground">
              Modelo de acceso mensual o anual que permite al usuario utilizar una serie de funcionalidades y contenidos
              en la plataforma mientras se mantenga activo el plan contratado.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Datos Personales</dt>
            <dd className="mt-1 text-muted-foreground">
              Toda información que permita identificar o hacer identificable a una persona natural, recolectada o
              tratada dentro del uso de la plataforma.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Términos y Condiciones</dt>
            <dd className="mt-1 text-muted-foreground">
              Este documento, que regula las obligaciones, derechos, limitaciones y alcances del uso de la plataforma y
              sus servicios.
            </dd>
          </div>
        </dl>

        <h3>3. Objeto del contrato</h3>
        <p>
          El presente contrato tiene por objeto regular el acceso, uso y condiciones bajo las cuales los usuarios pueden
          interactuar con la plataforma LIDER IA, operada por VIVETORI S.A.S., así como la adquisición de sus servicios
          de formación, acompañamiento y certificación en inteligencia artificial.
        </p>
        <p>Mediante la aceptación de estos Términos y Condiciones, el usuario podrá:</p>
        <ul>
          <li>Registrarse y crear una cuenta personal o corporativa.</li>
          <li>
            Acceder a los contenidos educativos ofrecidos por LIDER IA, según el plan de suscripción o ruta de
            certificación adquirido.
          </li>
          <li>
            Hacer uso de herramientas, guías y simuladores disponibles en la plataforma para el diseño, desarrollo o
            implementación de soluciones de inteligencia artificial.
          </li>
          <li>Participar en procesos de formación certificados ofrecidos por LIDER IA.</li>
          <li>
            Contactar con el equipo de soporte o consultoría, de acuerdo con las modalidades incluidas en su plan o
            producto contratado.
          </li>
        </ul>
        <p>
          Este contrato no otorga al usuario ningún derecho sobre los contenidos, marcas o funcionalidades de la
          plataforma más allá del acceso limitado previsto en la licencia de uso definida en el capítulo 7.
        </p>

        <h3>4. Registro y acceso a la plataforma</h3>
        <h4>4.1. Creación de cuentas y credenciales</h4>
        <p>
          Para acceder a los servicios de LIDER IA, el usuario deberá registrarse en la plataforma a través del sitio{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>, proporcionando
          información veraz, completa y actualizada.
        </p>
        <p>
          Cada cuenta es personal e intransferible. El usuario es responsable de custodiar sus credenciales (correo y
          contraseña) y se compromete a no compartirlas con terceros.
        </p>
        <p>
          Cualquier actividad realizada desde su cuenta se entenderá realizada por el titular, salvo que notifique de
          inmediato a LIDER IA sobre uso no autorizado o sospechas de vulneración.
        </p>
        <p>
          LIDER IA se reserva el derecho de verificar la identidad de los usuarios y, en caso de inconsistencias o uso
          indebido, suspender o cancelar cuentas sin previo aviso.
        </p>

        <h4>4.2. Requisitos de edad y capacidad</h4>
        <p>
          El uso de la plataforma está dirigido a personas mayores de edad (18 años o más). No obstante, menores de edad
          podrán acceder a los contenidos y rutas siempre que estén inscritos o supervisados por una persona adulta
          responsable, como parte de una ruta corporativa, institucional o educativa autorizado.
        </p>
        <p>
          Es responsabilidad del adulto garantizar el adecuado uso y cumplimiento de estos Términos y Condiciones por
          parte del menor bajo su cargo.
        </p>

        <h3>5. Planes de suscripción y rutas de certificación</h3>
        <h4>5.1. Tipos de planes</h4>
        <p>LIDER IA ofrece dos modelos principales de acceso a sus servicios:</p>
        <ul>
          <li>
            <strong>Planes de suscripción:</strong> Acceso continuo a contenidos y funcionalidades de la plataforma
            mediante pago mensual o anual. Los precios actualizados están disponibles en el sitio oficial{' '}
            <ExternalLink href="https://www.soylideria.com">www.soylideria.com</ExternalLink>.
          </li>
          <li>
            <strong>Rutas de certificación:</strong> Acceso a rutas de formación específicas mediante un pago único.
            Incluyen módulos estructurados, evaluaciones y entrega de certificado al completar satisfactoriamente el
            proceso.
          </li>
        </ul>
        <p>
          El contenido disponible, la duración y los beneficios incluidos pueden variar según el tipo de plan
          seleccionado. LIDER IA se reserva el derecho de actualizar o modificar la oferta de servicios, garantizando que
          los usuarios activos mantengan acceso a lo contratado.
        </p>

        <h4>5.2. Formas y condiciones de pago</h4>
        <p>Los pagos pueden realizarse mediante:</p>
        <ul>
          <li>Tarjeta de crédito (internacional).</li>
          <li>Transferencia bancaria, disponible exclusivamente los primeros 5 días de cada mes.</li>
        </ul>
        <p>
          Todos los cobros se realizan en dólares estadounidenses (USD). El usuario es responsable de cualquier comisión,
          cargo bancario o conversión aplicable según su entidad financiera o país.
        </p>

        <h4>5.3. Facturación y tributos</h4>
        <p>
          Salvo que se indique lo contrario, todos los precios incluyen los impuestos aplicables según la normativa
          vigente. Para efectos contables, los usuarios pueden solicitar factura o comprobante de pago escribiendo a{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>{' '}
          con los datos correspondientes.
        </p>

        <h3>6. Cancelación de suscripciones y política de reembolsos</h3>
        <h4>6.1. Cancelación de suscripciones</h4>
        <p>
          El usuario podrá cancelar en cualquier momento su suscripción mensual directamente desde la plataforma,
          accediendo a la sección de gestión de suscripción en{' '}
          <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>.
        </p>
        <p>
          La cancelación detiene la renovación automática para el siguiente periodo, pero no implica reembolso del periodo
          ya pagado. El acceso a los contenidos continuará activo hasta el final del ciclo de facturación vigente.
        </p>
        <p>
          En el caso de suscripciones anuales, la cancelación también puede realizarse en cualquier momento, pero el
          acceso y vigencia se mantendrán hasta la fecha de vencimiento del plan anual contratado.
        </p>

        <h4>6.2. Reembolsos</h4>
        <p>
          LIDER IA no ofrece reembolsos automáticos. Sin embargo, situaciones particulares podrán ser evaluadas a
          solicitud del usuario, quien deberá enviar su requerimiento al correo{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          , indicando:
        </p>
        <ul>
          <li>Motivo de la solicitud.</li>
          <li>Correo asociado a la cuenta.</li>
          <li>Fecha de pago y comprobante (si aplica).</li>
        </ul>
        <p>
          Cada caso será analizado individualmente, y LIDER IA se reserva el derecho de aceptar o rechazar la solicitud
          de reembolso según sus políticas internas.
        </p>

        <h3>7. Licencia de uso y propiedad intelectual</h3>
        <h4>7.1. Contenidos protegidos</h4>
        <p>
          Todos los contenidos desarrollados por LIDER IA —incluyendo cápsulas de aprendizaje, guías, simuladores,
          videos, textos, gráficas, metodologías y estructuras de las rutas— son propiedad intelectual de VIVETORI
          S.A.S. y están protegidos por las normas nacionales e internacionales sobre derechos de autor.
        </p>
        <p>
          Mediante la suscripción o participación en una ruta, el usuario obtiene una licencia de uso limitada, no
          exclusiva, intransferible y revocable para acceder y utilizar dichos contenidos únicamente con fines educativos
          y profesionales, conforme a lo establecido en estos Términos y Condiciones.
        </p>
        <p>
          Queda estrictamente prohibida la reproducción, distribución, transformación, sublicenciamiento o venta de los
          contenidos sin autorización previa y por escrito de LIDER IA.
        </p>

        <h4>7.2. Contenidos abiertos</h4>
        <p>
          Algunas plantillas, recursos descargables y ejemplos prácticos puestos a disposición dentro de la plataforma
          pueden estar catalogados como contenido abierto o reutilizable. En dichos casos, se indicará expresamente que
          pueden ser modificados o adaptados por el usuario para su uso interno.
        </p>
        <p>
          Esto no otorga ningún derecho sobre la marca, imagen o estructura general de la plataforma ni exime del
          cumplimiento del presente contrato.
        </p>

        <h4>7.3. Titularidad de proyectos desarrollados</h4>
        <p>
          Los proyectos, documentos o ejercicios desarrollados por el usuario en el marco de su experiencia formativa o
          de consultoría en la plataforma seguirán siendo propiedad del usuario. LIDER IA no reclama derechos sobre
          dichas creaciones.
        </p>

        <h4>7.4. Uso de proyectos como caso de éxito</h4>
        <p>
          El usuario autoriza a LIDER IA a referenciar su proyecto, experiencia o resultados como caso de éxito, en
          contextos académicos, comerciales o de divulgación, sin requerir aprobación previa. Esta autorización se otorga
          por el solo hecho de utilizar la plataforma y sus servicios, y podrá ser revocada en cualquier momento mediante
          solicitud escrita a{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          .
        </p>

        <h3>8. Confidencialidad</h3>
        <p>
          Todos los contenidos, procesos, estructuras metodológicas y herramientas ofrecidas por LIDER IA dentro de su
          plataforma se consideran información confidencial y de uso restringido.
        </p>
        <p>
          Asimismo, cualquier información que el usuario comparta dentro de la plataforma —incluyendo, pero sin
          limitarse a: ideas de proyectos, análisis internos, estrategias, documentos cargados, respuestas escritas o
          formularios completados— será tratada bajo estándares de confidencialidad y reserva, y no será divulgada
          públicamente sin autorización, salvo que:
        </p>
        <ul>
          <li>Sea información de conocimiento público;</li>
          <li>Haya sido obtenida legítimamente por terceros;</li>
          <li>Sea requerida por autoridad competente mediante orden legal.</li>
        </ul>

        <h4>8.1. Acuerdo de confidencialidad incorporado</h4>
        <p>
          Estos Términos y Condiciones incorporan un acuerdo de confidencialidad (NDA) de mutuo cumplimiento entre el
          usuario y LIDER IA. Por tanto, ambas partes se obligan a:
        </p>
        <ul>
          <li>
            No divulgar ni permitir el acceso de terceros a la información sensible compartida a través de la
            plataforma.
          </li>
          <li>
            Usar dicha información únicamente para los fines autorizados dentro del marco de la ruta o servicio
            contratado.
          </li>
        </ul>
        <p>Cualquier incumplimiento dará lugar a las acciones legales correspondientes.</p>

        <h3>9. Protección de datos personales</h3>
        <p>
          LIDER IA y VIVETORI S.A.S. se comprometen al cumplimiento integral de la Ley 1581 de 2012 y sus decretos
          reglamentarios sobre protección de datos personales en Colombia, así como del Reglamento General de Protección
          de Datos (GDPR) de la Unión Europea, cuando aplique.
        </p>
        <p>
          Para más información sobre el tratamiento de datos personales, consulta nuestra{' '}
          <a href="/privacy-policy" className="text-primary underline underline-offset-2">
            Política de Privacidad
          </a>
          .
        </p>

        <h3>10. Uso aceptable de la plataforma</h3>
        <p>
          El acceso a la plataforma LIDER IA debe realizarse de manera responsable, ética y conforme a la legislación
          vigente. El usuario se compromete a no utilizar los servicios para fines ilegales, fraudulentos, ofensivos o
          que afecten negativamente a otros usuarios o a la operación de la plataforma.
        </p>

        <h4>10.1. Actividades prohibidas</h4>
        <p>Está estrictamente prohibido:</p>
        <ul>
          <li>
            Copiar, distribuir, grabar, modificar o revender los contenidos protegidos por derechos de autor sin
            autorización expresa de LIDER IA.
          </li>
          <li>Compartir el acceso a la cuenta personal con terceros o utilizar credenciales ajenas.</li>
          <li>
            Ejecutar ingeniería inversa, descompilación o cualquier técnica destinada a descubrir el código fuente o
            estructura interna de la plataforma.
          </li>
          <li>
            Utilizar la plataforma para cargar, compartir o desarrollar contenidos que inciten al odio, discriminación,
            violencia, infracción de derechos de autor o que violen la ley.
          </li>
          <li>Acceder sin autorización a cuentas, datos, módulos o recursos no contratados o restringidos.</li>
          <li>
            Utilizar automatizaciones, scripts o herramientas externas que afecten el funcionamiento normal del sistema.
          </li>
        </ul>

        <h4>10.2. Suspensión o terminación de cuentas</h4>
        <p>
          LIDER IA se reserva el derecho de suspender temporal o definitivamente la cuenta de cualquier usuario que:
        </p>
        <ul>
          <li>Incurra en conductas prohibidas según lo establecido en este documento.</li>
          <li>
            Utilice indebidamente los contenidos, afecte la experiencia de otros usuarios o ponga en riesgo la seguridad
            de la plataforma.
          </li>
          <li>Robar o publicar sin autorización los contenidos internos o confidenciales de las rutas.</li>
        </ul>
        <p>
          La suspensión podrá realizarse sin previo aviso y no dará lugar a reembolsos. En casos graves, LIDER IA se
          reserva el derecho de emprender acciones legales.
        </p>

        <h3>11. Soporte y niveles de servicio</h3>
        <p>
          LIDER IA pone a disposición de sus usuarios canales de atención y soporte para resolver inquietudes técnicas,
          operativas o administrativas relacionadas con el uso de la plataforma.
        </p>

        <h4>11.1. Horarios y canales de atención</h4>
        <p>El soporte se brinda de lunes a viernes, en horario de oficina:</p>
        <ul>
          <li>
            <strong>Mañana:</strong> 8:00 a.m. – 12:00 p.m.
          </li>
          <li>
            <strong>Tarde:</strong> 2:00 p.m. – 6:00 p.m.
          </li>
        </ul>
        <p>Los usuarios pueden contactar al equipo de soporte a través de:</p>
        <ul>
          <li>
            <strong>Correo electrónico:</strong>{' '}
            <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
              hello@soylideria.com
            </a>
          </li>
          <li>
            <strong>Sección de soporte</strong> disponible dentro de la plataforma:{' '}
            <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>
          </li>
        </ul>
        <p>
          LIDER IA procura dar respuesta a todas las solicitudes en un plazo máximo de 2 días hábiles, sin que ello
          constituya una obligación de respuesta inmediata.
        </p>

        <h4>11.2. Mantenimiento programado e indisponibilidad</h4>
        <p>
          La plataforma podrá estar sujeta a mantenimientos técnicos programados, durante los cuales los servicios
          podrían estar parcial o totalmente indisponibles. En lo posible, estos mantenimientos serán informados
          previamente a los usuarios mediante correo electrónico o notificación dentro de la plataforma.
        </p>
        <p>
          En casos de fallas técnicas no programadas, el equipo técnico actuará con diligencia para restablecer el
          servicio en el menor tiempo posible. No obstante, LIDER IA no será responsable por interrupciones ocasionales
          derivadas de causas ajenas a su control.
        </p>

        <h3>12. Limitación de responsabilidad</h3>
        <p>
          LIDER IA ofrece sus servicios y contenidos &quot;tal como están&quot; y &quot;según disponibilidad&quot;, sin
          garantizar que estén libres de errores, interrupciones o resultados específicos. Aunque se realizan esfuerzos
          razonables para asegurar la calidad, actualidad y disponibilidad de la plataforma, el usuario acepta
          expresamente que su uso se realiza bajo su propio riesgo.
        </p>

        <h4>12.1. Alcance de la responsabilidad</h4>
        <p>
          En ningún caso LIDER IA (VIVETORI S.A.S.), sus representantes, empleados o aliados serán responsables por:
        </p>
        <ul>
          <li>
            Pérdidas de ingresos, beneficios, datos, oportunidades comerciales u otros daños indirectos, incidentales o
            consecuentes.
          </li>
          <li>
            Resultados derivados del uso, interpretación o aplicación de los contenidos formativos dentro de contextos
            empresariales o personales.
          </li>
          <li>
            Fallos de conectividad, incompatibilidades técnicas, o errores derivados del dispositivo, navegador o red del
            usuario.
          </li>
        </ul>

        <h4>12.2. Límite máximo de responsabilidad</h4>
        <p>
          La responsabilidad total de LIDER IA frente al usuario, por cualquier causa, se limitará en todo caso al monto
          efectivamente pagado por el usuario en los últimos 12 meses anteriores al evento que dio origen a la
          reclamación.
        </p>

        <h3>13. Modificaciones de los Términos y Condiciones</h3>
        <p>
          LIDER IA se reserva el derecho de modificar, actualizar o complementar en cualquier momento y sin previo aviso
          estos Términos y Condiciones, con el fin de ajustarlos a cambios legales, operativos o de funcionamiento de la
          plataforma.
        </p>
        <p>
          Cualquier modificación entrará en vigor desde el momento en que sea publicada en la plataforma, y se entenderá
          aceptada por el usuario con la continuidad en el uso del servicio.
        </p>
        <p>Cuando los cambios sean sustanciales o afecten directamente los derechos adquiridos del usuario, LIDER IA podrá notificarlo mediante:</p>
        <ul>
          <li>Correo electrónico registrado.</li>
          <li>Mensaje destacado dentro de la plataforma.</li>
          <li>Actualización visible en el sitio web oficial.</li>
        </ul>

        <h3>14. Ley aplicable y jurisdicción</h3>
        <p>
          El presente acuerdo se rige por las leyes de la República de Colombia, sin perjuicio de las disposiciones
          legales imperativas que resulten aplicables en la jurisdicción del usuario.
        </p>
        <p>
          Para todos los efectos legales derivados del uso de la plataforma LIDER IA y la relación contractual con
          VIVETORI S.A.S., las partes se someten a la jurisdicción de los jueces de la ciudad de Barranquilla, Colombia,
          renunciando expresamente a cualquier otro fuero que pudiera corresponderles por razón de su domicilio presente
          o futuro.
        </p>
        <p>
          Sin embargo, LIDER IA podrá, a su criterio, acudir previamente a mecanismos alternativos de resolución de
          conflictos como la conciliación ante centros autorizados por la Cámara de Comercio de Barranquilla.
        </p>

        <h3>15. Comunicaciones comerciales y uso del correo electrónico</h3>
        <p>
          Al registrarse en la plataforma LIDER IA, el usuario autoriza a VIVETORI S.A.S. a enviarle comunicaciones
          electrónicas relacionadas con:
        </p>
        <ul>
          <li>Actualizaciones de la plataforma y sus servicios.</li>
          <li>Recordatorios de acceso, renovaciones o vencimientos.</li>
          <li>Contenidos educativos relevantes.</li>
          <li>Invitaciones a eventos, webinars, rutas y novedades.</li>
          <li>
            Ofertas promocionales o comerciales asociadas a la temática de inteligencia artificial, formación o
            innovación.
          </li>
        </ul>
        <p>
          Estas comunicaciones se enviarán al correo electrónico registrado por el usuario. En todo momento, el usuario
          podrá ejercer su derecho a no recibir comunicaciones comerciales adicionales, utilizando el enlace de
          desuscripción incluido en cada mensaje o escribiendo a{' '}
          <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
            hello@soylideria.com
          </a>
          .
        </p>

        <h3>16. Contacto y notificaciones oficiales</h3>
        <p>
          Para cualquier solicitud, notificación legal, inquietud sobre estos Términos y Condiciones o ejercicio de
          derechos relacionados con el uso de la plataforma, los usuarios podrán comunicarse directamente con el equipo de
          LIDER IA a través de los siguientes medios oficiales:
        </p>
        <ul>
          <li>
            <strong>Correo electrónico:</strong>{' '}
            <a href="mailto:hello@soylideria.com" className="text-primary underline underline-offset-2">
              hello@soylideria.com
            </a>
          </li>
          <li>
            <strong>Sitio web:</strong>{' '}
            <ExternalLink href="https://www.soylideria.com">https://www.soylideria.com</ExternalLink>
          </li>
          <li>
            <strong>Plataforma de uso:</strong>{' '}
            <ExternalLink href="https://app.soylideria.com">https://app.soylideria.com</ExternalLink>
          </li>
        </ul>
        <p>
          Las notificaciones se considerarán válidas cuando sean enviadas desde o hacia estos canales. Cualquier cambio
          en los datos de contacto será informado oportunamente a través de la plataforma.
        </p>
      </article>
    </div>
  );
}
